import { AxiosError, AxiosResponse } from 'axios';

import {
  API_ENDPOINT_KEYS,
  SERVICE_AUTH_ENDPOINTS,
  SESSION_ENDED_TOAST,
  SESSION_ENDED_TOAST_ID,
} from '~common';
import { AUTH_API_CLIENT_ID } from '~configs';
import { SELECTED_GROUP_ID } from '~providers';
import { postAuthRequest } from '~services/auth.service';
import { LoggedInUserInfo, UserInfo } from '~types';

const ONE_DAY_IN_SECONDS = 86400;
export const REFRESH_TOKEN_LS_KEY = 'cf-dashboard-refresh_token';
export const USER_INFO_LS_KEY = 'cf-dashboard-user_info';
export const USER_LOGOUT_EVENT = 'cf-dashboard-logout';

export const inMemoryJWTManager = () => {
  let inMemoryJWT: string | null = null;
  let jwtExpiresAt: number | null = null;
  let userInfo: UserInfo | LoggedInUserInfo | null = null;
  let isRefreshingToken = false;
  let refreshPromise: Promise<string> | null = null;

  const setRefreshToken = (token: string) => {
    localStorage.setItem(REFRESH_TOKEN_LS_KEY, token);
  };

  // The method makes a call to the refresh-token endpoint
  // If there is a valid cookie, the endpoint will return a fresh JWT.
  const getNewAccessToken = async (): Promise<string> => {
    // always get refresh token from local storage for persistence between multiple tabs
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_LS_KEY) ?? '';

    // refresh token doesn't exist in local storage, return
    if (!refreshToken) {
      return Promise.reject('Not logged in.');
    }

    const request = postAuthRequest({
      path: SERVICE_AUTH_ENDPOINTS[API_ENDPOINT_KEYS.REFRESH_TOKEN],
      data: {
        refresh_token: refreshToken,
        grant_type: 'refresh_token',
        clientId: AUTH_API_CLIENT_ID,
      },
      ignoreAuthHeader: true,
    });
    return request
      .then((response: AxiosResponse) => {
        return response.data;
      })
      .then((userInfo: UserInfo) => {
        if (userInfo.accessToken) {
          setToken(userInfo.accessToken, userInfo.expiresIn);
        }
        if (userInfo.refreshToken) {
          setRefreshToken(userInfo.refreshToken);
        }

        return userInfo.accessToken
          ? Promise.resolve(userInfo.accessToken)
          : Promise.reject('No access token returned.');
      })
      .catch((err) => {
        eraseToken();
        window.dispatchEvent(new Event('logoutEvent'));
        // eslint-disable-next-line no-console
        console.error("Log out because we can't renew the token.");
        throw new AxiosError(err.message, err.code, err.config, err.request, {
          ...err.response,
          data: {
            // Update the data object
            ...err.response?.data,
            message: SESSION_ENDED_TOAST,
            toastId: `${SESSION_ENDED_TOAST_ID}-${Math.random()}`,
          },
        });
      });
  };

  // The method returns the current token if it's not expired,
  // otherwise it will refresh the token and return the new one.
  const getValidToken = async (): Promise<string> => {
    if (jwtExpiresAt && jwtExpiresAt < Date.now()) {
      // Prevent multiple token refresh attempts by checking and
      // setting isRefreshingToken to true
      if (!isRefreshingToken) {
        isRefreshingToken = true;
        refreshPromise = getNewAccessToken().finally(() => {
          isRefreshingToken = false;
          refreshPromise = null;
        });
      }

      // refreshPromise ensures that the token being refreshed is
      // consistently returned across concurrent calls
      if (refreshPromise) {
        return refreshPromise;
      }
    }

    return inMemoryJWT || Promise.reject('No available access token.');
  };

  const getCurrentToken = () => inMemoryJWT;

  const getUserInfo = () => userInfo;

  const setToken = (token: string, expiresIn: number = ONE_DAY_IN_SECONDS) => {
    inMemoryJWT = token;
    jwtExpiresAt = Date.now() + Math.max(expiresIn - 10, 0) * 1000; // expiry time minus 10 seconds
  };

  const setUserInfo = (info: UserInfo | LoggedInUserInfo) => {
    userInfo = info;
    localStorage.setItem(USER_INFO_LS_KEY, JSON.stringify(info));
  };

  const eraseToken = () => {
    localStorage.removeItem(REFRESH_TOKEN_LS_KEY);
    localStorage.removeItem(USER_INFO_LS_KEY);
    localStorage.removeItem(SELECTED_GROUP_ID);

    window.localStorage.setItem(USER_LOGOUT_EVENT, String(Date.now()));

    inMemoryJWT = null;
    userInfo = null;
    jwtExpiresAt = null;
    isRefreshingToken = false;
    refreshPromise = null;
  };

  return {
    getUserInfo,
    setUserInfo,
    userInfo,
    eraseToken,
    getValidToken,
    getCurrentToken,
    setToken,
    setRefreshToken,
    getNewAccessToken,
  };
};

export const inMemoryJWTService = inMemoryJWTManager();
